import moment from 'moment-timezone';

import {type Child, type Plan} from './plan-types';
import {type DateRangeExclusive, testOverlap, testEndsBefore} from './date-range';

/**
 * Adds a break (empty plan) to a child for a given date period. Optionally
 * adds a continued plan after the break.
 * @param child The child to add a break to.
 * @param breakRange The date range of the break.
 * @param continuePlan True iff any interrupted plan should be continued
 * after the break.
 * @returns The child with the break (and continued plan) added.
 */
export const addBreakToChild = (child: Child, breakRange: DateRangeExclusive, continuePlan: boolean): Child => {
    const sortedPlans = sortPlansDescendingByDate(child.plans);
    return {...child, plans: addBreakToPlansForChild(sortedPlans, breakRange, continuePlan)};
};

const addBreakToPlansForChild = (plans: Plan[], breakRange: DateRangeExclusive, continuePlan: boolean): Plan[] => {
    // Does breaks make sense for children without plans? (GRO-2888)
    if (plans.length === 0) {
        return [];
    }
    if (continuePlan) {
        const plansToBreak = plans.filter(doesBreakPlan(breakRange));
        return [makeBreakPlan(breakRange, plans[0])].concat(plansToBreak.map(makeContinuedPlan(breakRange)));
    } else {
        return [makeBreakPlan(breakRange, plans[0])];
    }
};

/**
 * Tests if a date range for a break interrupts a plan in such a way that it
 * should be continued after the break. I.e. if the break overlaps the plan
 * and there are days in the plan after the break.
 * @param breakRange The range for the break.
 * @param plan The plan to test against.
 * @returns True iff the break interrupts the plan.
 */
const doesBreakPlan =
    (breakRange: DateRangeExclusive) =>
    (plan: Plan): boolean => {
        const planRange = getPlanRange(plan);
        return testOverlap(breakRange, planRange) && testEndsBefore(breakRange, planRange);
    };

const getPlanRange = (plan: Plan): DateRangeExclusive => {
    return {
        fromInclusive: moment(plan.from),
        toExclusive: plan.to !== null ? moment(plan.to) : null,
    };
};

/**
 * Constructs an empty plan for a break.
 * @param breakRange The date range of the break.
 * @param prototypePlan A prototype of a plan to pick other fields from.
 * @returns An empty plan for a break.
 */
const makeBreakPlan = (breakRange: DateRangeExclusive, prototypePlan: Plan): Plan => {
    const prototypePart = prototypePlan.planParts[0];
    const breakPlan = {
        ...prototypePlan,
        planParts: [
            {
                ...prototypePart,
                sessionBookings: [],
                productBookings: [],
                discounts: [],
            },
        ],
        from: breakRange.fromInclusive.format('YYYY-MM-DD'),
        to: breakRange.toExclusive?.format('YYYY-MM-DD') ?? null,
    };
    return breakPlan;
};

/**
 * Constructs a continued plan after a break.
 * @param breakRange The date range of the break.
 * @param plan The plan that is interrupted by the break.
 * @returns The continued plan.
 */
const makeContinuedPlan =
    (breakRange: DateRangeExclusive) =>
    (plan: Plan): Plan => ({
        ...plan,
        from: breakRange.toExclusive!.format('YYYY-MM-DD'),
        to: plan.to,
    });

/**
 * Sorts plans in descending order with the latest plan first.
 * @param plans Array of plans to sort
 * @returns Sorted array of plans with latest first
 */
const sortPlansDescendingByDate = (plans: Plan[]): Plan[] => {
    return [...plans].sort((a, b) => {
        const dateA = moment(a.from);
        const dateB = moment(b.from);
        return dateB.valueOf() - dateA.valueOf();
    });
};

/**
 * Replaces all occurrences of 'plans' with 'breaks' and 'plan' with 'break' in a string.
 * @param input The input string to process.
 * @returns The string with replacements applied.
 */
export const replacePlanWithBreakWord = (errorMessage: string): string => {
    return errorMessage.replace(/plans/g, 'breaks').replace(/plan/g, 'break');
};
